import React, { useState } from 'react';
import { X } from 'lucide-react';
import type {
  MarketingRequestPriority,
  MarketingRequestType,
} from '../../types/marketing';

export interface NewMarketingRequestFormValues {
  title: string;
  description: string;
  requestType: MarketingRequestType;
  priority: MarketingRequestPriority;
  dueDate: string;
}

interface NewMarketingRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: NewMarketingRequestFormValues) => Promise<void>;
  saving: boolean;
  error: string | null;
}

const TYPE_OPTIONS: { value: MarketingRequestType; label: string }[] = [
  { value: 'content', label: 'Content' },
  { value: 'social', label: 'Social' },
  { value: 'email', label: 'Email' },
  { value: 'graphic', label: 'Graphic' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS: { value: MarketingRequestPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const NewMarketingRequestModal: React.FC<NewMarketingRequestModalProps> = ({
  open,
  onClose,
  onSubmit,
  saving,
  error,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requestType, setRequestType] = useState<MarketingRequestType>('content');
  const [priority, setPriority] = useState<MarketingRequestPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!title.trim()) {
      setLocalError('Title is required.');
      return;
    }
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        requestType,
        priority,
        dueDate,
      });
      setTitle('');
      setDescription('');
      setRequestType('content');
      setPriority('medium');
      setDueDate('');
    } catch {
      /* parent sets error */
    }
  };

  const displayError = localError || error;

  return (
    <div className="mh-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mh-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-mreq-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="mh-modal-header">
          <h2 id="new-mreq-title">New content request</h2>
          <button type="button" className="mh-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form className="mh-modal-form" onSubmit={handleSubmit}>
          {displayError && <p className="mh-modal-error" role="alert">{displayError}</p>}

          <label className="mh-field">
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              autoFocus
              disabled={saving}
            />
          </label>

          <label className="mh-field">
            <span>Description</span>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              disabled={saving}
            />
          </label>

          <div className="mh-field-row">
            <label className="mh-field">
              <span>Type</span>
              <select value={requestType} onChange={e => setRequestType(e.target.value as MarketingRequestType)} disabled={saving}>
                {TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="mh-field">
              <span>Priority</span>
              <select value={priority} onChange={e => setPriority(e.target.value as MarketingRequestPriority)} disabled={saving}>
                {PRIORITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="mh-field">
            <span>Due date (optional)</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={saving} />
          </label>

          <div className="mh-modal-actions">
            <button type="button" className="mh-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="mh-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewMarketingRequestModal;
